import createService from './factory';
import { FirestoreCollectionIDs } from 'core/constants/firestore-collection-ids';
import { ILocationDto, ILocationDao } from './types/locations.interface';
import { ISubLocationDao, ISubLocationDto } from './types/sub-locations.interface';
import { IVehicleDao, IVehicleDto } from './types/vehicles.interface';
import { IVehicleCheckDao, IVehicleCheckDto } from './types/vehicle-checks.interface';
import { IRunDao, IRunDto, IRunLocationDao, IRunLocationDto } from './types/runs.interface';
import { IAlertDao, IAlertDto } from './types/alerts.interface';
import { IDeliveryDao, IDeliveryDto } from './types/deliveries.interface';
import { ISettingsVehicleCheckDao, ISettingsVehicleCheckDto } from './types/settings-vehicle-checks.interface';
import { IUserDao, IUserDto } from './types/users.interface';
import { IProcessTypeDao, IProcessTypeDto } from './types/process-types.interface';
import { IProcessTypeStatusDao, IProcessTypeStatusDto } from './types/process-type-statuses.interface';
import { ILogisticsMetaDao, ILogisticsMetaDto } from './types/meta.interface';
import { IRunActivityDao, IRunActivityDto } from './types/run-activity.interface';
import { IShiftActivityDao, IShiftActivityDto } from './types/shift-activity.interface';
import { IConsignmentDao, IConsignmentDto } from './types/consignments.interface';
import { ITransportJobDao, ITransportJobDto } from './types/transport-job.interface';
import { IRdcConsignmentDao, IRdcConsignmentDto } from './types/rdc.interface';
import { IDeliveryRequestDao, IDeliveryRequestDto } from './types/delivery-requests.interface';
import ApiService from './api.service';
import {
  IJotformSettingsDao,
  IJotformSettingsDto,
  IJotformSubmissionDao,
  IJotformSubmissionDto,
} from './types/jotform.interface';
import { storage } from 'core/config/firebase';
import { ref } from '@firebase/storage';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';

/** Read-only in the portal: every write goes through a cloud function. */
export const DeliveryRequestsApiService = createService<IDeliveryRequestDto, IDeliveryRequestDao>(
  FirestoreCollectionIDs.DELIVERY_REQUESTS,
);

/** Dev only, and read-only: the webhook and `devJotformRelay` do the writing. */
export const JotformSubmissionsApiService = createService<IJotformSubmissionDto, IJotformSubmissionDao>(
  FirestoreCollectionIDs.JOTFORM_SUBMISSIONS,
);
export const WebhookSettingsApiService = createService<IJotformSettingsDto, IJotformSettingsDao>(
  FirestoreCollectionIDs.WEBHOOK_SETTINGS,
);

export const ProcessTypesApiService = createService<IProcessTypeDto, IProcessTypeDao>(
  FirestoreCollectionIDs.PROCESS_TYPES,
);
export const ProcessTypeStatusesApiService = createService<IProcessTypeStatusDto, IProcessTypeStatusDao>(
  FirestoreCollectionIDs.PROCESS_TYPE_STATUSES,
);
export const UsersApiService = createService<IUserDto, IUserDao>(FirestoreCollectionIDs.USERS);
export const DeliveriesApiService = createService<IDeliveryDto, IDeliveryDao>(FirestoreCollectionIDs.DELIVERIES);
export const LocationsApiService = createService<ILocationDto, ILocationDao>(FirestoreCollectionIDs.LOCATIONS);
export const SubLocationsApiService = createService<ISubLocationDto, ISubLocationDao>(
  FirestoreCollectionIDs.SUB_LOCATIONS,
);
export const SettingsVehicleChecksApiService = createService<ISettingsVehicleCheckDto, ISettingsVehicleCheckDao>(
  FirestoreCollectionIDs.SETTINGS_VEHICLE_CHECKS,
);
export const VehiclesApiService = createService<IVehicleDto, IVehicleDao>(FirestoreCollectionIDs.VEHICLES);
export const VehicleChecksApiService = createService<IVehicleCheckDto, IVehicleCheckDao>(
  FirestoreCollectionIDs.VEHICLE_CHECKS,
);
export const RunsApiService = createService<IRunDto, IRunDao>(FirestoreCollectionIDs.RUNS);
export const RunLocationsApiService = createService<IRunLocationDto, IRunLocationDao>(
  FirestoreCollectionIDs.RUN_LOCATIONS,
);
export const AlertsApiService = createService<IAlertDto, IAlertDao>(FirestoreCollectionIDs.ALERTS);
export const MetaApiService = createService<ILogisticsMetaDto, ILogisticsMetaDao>(FirestoreCollectionIDs.META);
export const RunActivityApiService = createService<IRunActivityDto, IRunActivityDao>(
  FirestoreCollectionIDs.RUN_ACTIVITY,
);
export const ShiftActivityApiService = createService<IShiftActivityDto, IShiftActivityDao>(
  FirestoreCollectionIDs.SHIFT_ACTIVITY,
);
export const ConsignmentsApiService = createService<IConsignmentDto, IConsignmentDao>(
  FirestoreCollectionIDs.CONSIGNMENTS,
);
export const RdcConsignmentsApiService = {
  ...createService<IRdcConsignmentDto, IRdcConsignmentDao>(FirestoreCollectionIDs.RDC_CONSIGNMENTS),
  getPhotoUrl: async (path: string) => {
    const imageRef = ref(storage, path);
    return ApiService.getFileDownloadUrl(imageRef);
  },
  getSignatureUrl: async (uid: string) => {
    const imageRef = ref(storage, `deliverySignature/${uid}.png`);
    return ApiService.getFileDownloadUrl(imageRef);
  },
};
export const TransportJobsApiService = {
  ...createService<ITransportJobDto, ITransportJobDao>(FirestoreCollectionIDs.TRANSPORT_JOBS),
  getSignature: async (uid: string) => {
    const imageRef = ref(storage, `deliverySignature/${uid}.png`);
    return ApiService.getFileDownloadUrl(imageRef);
  },
  export: async (startDateString: string, endDateString: string, type: 'csv' | 'pdf'): Promise<string> => {
    return ApiService.callFunction(
      {
        startDateString,
        endDateString,
        type,
      },
      FirebaseFunctionNames.EXPORT_TRANSPORT_JOBS,
    );
  },
};
